// Internal modules
import { getGlobals, getLocale, logger } from '@/lib';
import { logPrepareCookieBannerToFPTI } from '../fpti';

// Exported for testing purposes only
export const CONTAINER_ID = 'cookie-banner-container';
const BODY_CLASS_NAME_REG_EXP = /\b\w+CookieBanner-acceptedAll\b/g;
const HTML_TAG_REG_EXP = /^<[a-z]+ nonce=".*">(.+)<\/[a-z]+>$/;

export const removeCookieBanner = (): void => {
  const { country, language } = getLocale();

  // `div#cookie-banner-container` is the container we have in our index.html
  // file that we inject the cookie banner scripts into
  const containerEl = document.getElementById(CONTAINER_ID);

  // fix misalignment of cookie banner in new header
  document.body.style.paddingTop = '0px';

  if (!containerEl?.firstChild) {
    logger.info('no_cookie_banner_to_remove', { country, language });
    return;
  }

  document.body.className = document.body.className
    .replace(BODY_CLASS_NAME_REG_EXP, '')
    .trim();

  while (containerEl.firstChild) {
    containerEl.removeChild(containerEl.firstChild);
  }

  logger.info('cookie_banner_removed', { country, language });
};

// Exported for testing purposes only
export const createCookieBannerElement = (
  str: string,
  tagName: string,
): HTMLElement => {
  const el = document.createElement(tagName);

  // `commonutils-client` returns the script and styles as a string. We need
  // to strip the tags from the string so we are only inserting the code.
  el.textContent = str.replace(HTML_TAG_REG_EXP, '$1');
  el.nonce = getGlobals().nonce;

  return el;
};

export const setCookieBanner = ({
  css,
  html,
  js,
}: {
  css: string;
  html: string;
  js: string;
}): void => {
  const { country, language } = getLocale();
  if (!css || !html || !js) {
    logger.info('cookie_banner_scripts_not_available', {
      country,
      language,
    });
    return;
  }

  let containerEl = document.getElementById(CONTAINER_ID);
  const fragment = document.createDocumentFragment();

  const markupContainer = document.createElement('div');
  markupContainer.innerHTML = html;

  /**
   * START @TECH_DEBT
   * There is a bug in cookieserv that results in the commonutils-client
   * returning cookie banner data for countries that shouldn't show the cookie
   * banner. For some countries there won't even be any text for the cookie
   * banner. In that case we won't show the banner. Once this bug is fixed
   * on cookieserv we can remove this check.
   *
   * Ticket for cookie banner bug:
   * https://engineering.paypalcorp.com/jira/browse/DTCONPRI-1167
   */
  const hasContent = Boolean(markupContainer.textContent?.length);
  logPrepareCookieBannerToFPTI(hasContent);

  if (!hasContent) {
    logger.info('cookie_banner_content_missing');
    return;
  }
  /**
   * END @TECH_DEBT
   */

  const { nonce } = getGlobals();
  const bannerEl = markupContainer.firstElementChild;
  const styleEl = createCookieBannerElement(css, 'style');
  const scriptEl = createCookieBannerElement(js, 'script');

  styleEl.setAttribute('nonce', nonce);
  scriptEl.setAttribute('nonce', nonce);
  if (bannerEl) {
    fragment.appendChild(bannerEl);
  }
  fragment.appendChild(styleEl);
  fragment.appendChild(scriptEl);

  // If the container element doesn't exists for some reason we need to create
  // it so we don't cause an error trying to call `.appendChild()` on `null`.
  // This should never happen but that probably means it will... ¯\_(ツ)_/¯
  if (!containerEl) {
    logger.info('cookie_banner_container_not_found');
    containerEl = document.createElement('div');
    containerEl.id = CONTAINER_ID;
    document.body.appendChild(containerEl);
  }

  containerEl.appendChild(fragment);
  logger.info('cookie_banner_inserted_into_dom', {
    country,
    language,
  });
};
